import { WorkflowStepErrorResponse } from '@shared/models';

/**
 * KOR's code for a sign request the e-signature provider refused outright (KOR-15259) - typically a
 * template that prefills a value of the wrong type into one of its fields. The business has to fix
 * the template; nothing was saved, so the step works again once it has.
 */
export const SIGN_HERE_REQUEST_REJECTED = 'signhere-request-rejected';

/**
 * KOR's code for a sign-here step whose signer could not be resolved from the customer record - a
 * business with no contact person able to sign. Same shape and the same remedy as above: the
 * business fixes the record and the step works on the next try.
 */
export const SIGN_HERE_SIGNER_NO_CONTACT = 'signhere-signer-no-contact';

/** The sign-here failures the customer can wait out on the step itself, rather than a dead end. */
export const SIGN_HERE_RECOVERABLE_ERROR_CODES: ReadonlySet<string> = new Set([SIGN_HERE_REQUEST_REJECTED, SIGN_HERE_SIGNER_NO_CONTACT]);

const DEFAULT_SIGN_HERE_MESSAGE = 'We could not prepare your document for signing. Please contact the business that sent you this request.';

const bodyOf = (errorRes: unknown): WorkflowStepErrorResponse =>
  ((errorRes as { error?: WorkflowStepErrorResponse } | undefined)?.error ?? {}) as WorkflowStepErrorResponse;

/**
 * The customer-facing message for a sign-here step the backend could not prepare yet, or `undefined`
 * for any other failure.
 *
 * KOR writes these two messages for the end customer - they name no provider, field or id - so they
 * are shown as sent. Deliberately narrow, like `debitAuthorizationCurrencyError`: any other code
 * still reaches the caller's own handling instead of putting KOR's `message` in front of the customer.
 */
export const signHereRecoverableError = (errorRes: unknown): string | undefined => {
  const body = bodyOf(errorRes);

  if (!SIGN_HERE_RECOVERABLE_ERROR_CODES.has(body.errorCode ?? '')) {
    return undefined;
  }

  return body.message?.trim() ? body.message : DEFAULT_SIGN_HERE_MESSAGE;
};
